import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { showSuccessWorkerClassificationPopup } from '~/actions';
import { locationSettingsKeys } from '~/domain/LocationSettings';
import { showErrorMessage } from '~/domain/utils';
import { saveOfficeWorkerClassification } from '../clients/SaveOfficeWorkerClassification';
import type { ISaveOfficeWorkerClassificationApiResponse } from '../types/SaveWorkerType.api';
import type { ISaveOfficeWorkerTypeRequest } from '../types/SaveWorkerType';

/**
 * Saves the worker type of a location other than the selected office, from its
 * Location Settings page (GOTU-19472). A settings save, not a sign-up: unlike
 * `useSaveWorkerType` it marks nothing complete and fires no sign-up event. It
 * refreshes that location's record and raises the same success popup as the
 * selected office's save, with the server's verdict (confirmed, or pending the
 * oversight account's approval) and without writing the signed-in `user`.
 */
export const useSaveOfficeWorkerClassification = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    ISaveOfficeWorkerClassificationApiResponse,
    unknown,
    ISaveOfficeWorkerTypeRequest
  >({
    mutationFn: ({ officeId, workerType }) =>
      saveOfficeWorkerClassification(officeId, { type: workerType }),
    onSuccess: (response, { officeId }) => {
      queryClient.invalidateQueries({ queryKey: locationSettingsKeys.office(officeId) });
      const isPendingApproval = response.latestWorkerClassificationRequest?.status === 'requested';
      dispatch(showSuccessWorkerClassificationPopup(isPendingApproval ? 'requested' : 'confirmed'));
    },
    onError: (error) => showErrorMessage({ error }),
  });
};
